import { defineStore } from "pinia";
import { useSecret } from "~/composables/useSecret";
import { decrypt } from '../utility/encryption.js';
import { GoogleAnalytics } from "~/google_analytics/events.js";

interface filter {
  date: string | null;
  status: Array<string> | null;
  category: Array<string> | null;
}

interface GiftCart {
  cartId: string | null,
  transaction_id: string | null;
  items: GiftCardItem[];
  response: { data: any; error: any; oosError: [] };
  loader: boolean;
  loadingMessage: string | "";
  filter: filter;
}

interface selectedDenomination {
  itemId: any;
  quantity: any;
  denomination: any;
  denominationId: any;
}

interface GiftCardItem {
  giftCard: any;
  selectedDenominations: selectedDenomination[];
  receiverName: any;
  recipientEmail: any;
  recipientPhone: any;
  senderEmail: any;
  senderPhone: any;
  message: any;
  payAmount: any;
}

const generateCartId = (): string => {
  const timestamp = Date.now();
  const randomNum = Math.floor(Math.random() * 1_000_000_000).toString().padStart(9, '0');
  const randomStr = Math.random().toString(36).substring(2, 8);
  return `cart_${timestamp}_${randomNum}_${randomStr}`;
};


export const useGiftCart = defineStore('giftcart', {
  state: (): GiftCart => ({
    cartId: null,
    transaction_id: null,
    items: [],
    response: { data: null, error: null, oosError: [] },
    loader: false,
    loadingMessage: "Please wait while we process your request...",
    filter: {
      date: null,
      status: [],
      category: []
    },
  }),

  getters: {

    itemCount(state: GiftCart): number {
      return state.items.reduce((count: number, item: any) => {
        return count + (item.selectedDenominations?.length || 0);
      }, 0);
    },

    itemTotal(state: GiftCart): number {
      const total = state.items.reduce((sum, item) => {
        const subTotal = (item.selectedDenominations || []).reduce((innerSum: any, subItem: any) => {
          const denomination = parseFloat(subItem.denomination) || 0;
          const quantity = parseInt(subItem.quantity) || 0;
          return innerSum + (denomination * quantity);
        }, 0);
        return sum + subTotal;
      }, 0);

      return parseFloat(total.toFixed(2));
    },


    itemTotalWithDiscount(state: GiftCart): number {
      const total = state.items.reduce((sum, item) => {
        const subTotal = (item.selectedDenominations || []).reduce((innerSum, subItem) => {
          const denomination = parseFloat(subItem.denomination) || 0;
          const quantity = parseInt(subItem.quantity) || 0;
          return innerSum + denomination * quantity;
        }, 0);

        // Apply discount if available
        const discount = parseFloat(item.giftCard?.defaultDiscount) || 0;
        const discountedTotal = subTotal - (subTotal * discount) / 100;

        return sum + discountedTotal;
      }, 0);

      return parseFloat(total.toFixed(2));
    },

    itemsDiscountAmount(state: GiftCart): number {
      const totalDiscount = state.items.reduce((sum: number, item: any) => {
        const discount = parseFloat(item.giftCard.defaultDiscount);
        if (!discount || isNaN(discount)) return sum; // skip if no discount

        const subTotalDiscount = (item.selectedDenominations || []).reduce((innerSum: number, subItem: any) => {
          const denomination = parseFloat(subItem.denomination) || 0;
          const quantity = parseInt(subItem.quantity) || 0;
          const itemDiscount = denomination * quantity * (discount / 100);
          return innerSum + itemDiscount;
        }, 0);

        return sum + subTotalDiscount;
      }, 0);

      return -parseFloat(totalDiscount.toFixed(2));
    },


    filterCount(state: GiftCart): number {
      let count = 0;

      if (state.filter.date) count += 1;
      count += state.filter.status?.length ?? 0;
      count += state.filter.category?.length ?? 0;

      return count;
    }

  },

  actions: {
    addFilter(data: filter) {
      this.filter.date = data.date;
      this.filter.status = data.status;
      this.filter.category = data.category;
    },
    removeFilter() {
      this.filter = {
        date: null,
        status: [],
        category: [],
      };
    },

    addItemToCart(giftCardItem: GiftCardItem) {
      // Generate a new cartId if items are currently empty
      this.cartId = generateCartId();
      this.items = [giftCardItem];
      const denomination = Number(giftCardItem.selectedDenominations?.[0]?.denomination || 0);
      const quantity = Number(giftCardItem.selectedDenominations?.[0]?.quantity || 0);
      const discountPercent = Number(giftCardItem.giftCard?.defaultDiscount || 0);

      const totalAmount = denomination * quantity;
      const discountAmount = discountPercent > 0
        ? (totalAmount * discountPercent) / 100
        : 0;

      const finalAmount = totalAmount - discountAmount;

      const eventPayload = {
        currency: 'INR',
        value: finalAmount, 
        items: [
          {
            item_id: giftCardItem.giftCard?.id || '',
            item_name: giftCardItem.giftCard?.name || '',
            item_category:
              giftCardItem.giftCard?.category?.name ||
              giftCardItem.giftCard?.category ||
              undefined,
            item_brand: giftCardItem.giftCard?.name || undefined,
            price: denomination, // per unit price
            quantity: quantity,  // ⚠️ FIXED (was wrong before)
            discount: discountAmount || undefined, // optional but recommended
          },
        ],
      };
      GoogleAnalytics.addToCart(eventPayload);
    },

    removeItemFromCart(giftCardItem: GiftCardItem) {
      // this.items = this.items.filter(
      //   (item) => !(item.id === giftCardItem.id)
      // );
    },

    removeItemFromCartByItem(selectedItem: selectedDenomination) {
      if (!this.items?.length) return;

      const cardIndex = this.items.findIndex(
        ({ giftCard }) => giftCard.id === selectedItem.itemId
      );
      if (cardIndex === -1) return;

      const targetCard = this.items[cardIndex];
      const { selectedDenominations } = targetCard;

      const denomIndex = selectedDenominations.findIndex(
        ({ denominationId }) => denominationId === selectedItem.denominationId
      );
      if (denomIndex === -1) return;

      // Always remove the denomination entirely
      selectedDenominations.splice(denomIndex, 1);

      // If no denominations left, remove the entire gift card item
      if (selectedDenominations.length === 0) {
        this.items.splice(cardIndex, 1);
      }

      const denomination = Number(targetCard.selectedDenominations?.[0]?.denomination || 0);
      const quantity = Number(targetCard.selectedDenominations?.[0]?.quantity || 0);
      const discountPercent = Number(targetCard.giftCard?.defaultDiscount || 0);

      const totalAmount = denomination * quantity;

      // Calculate discount
      const discountAmount =
        discountPercent > 0
          ? (totalAmount * discountPercent) / 100
          : 0;

      const finalAmount = totalAmount - discountAmount;

      const eventPayload = {
        currency: 'INR',
        value: finalAmount, // ✅ Final payable amount (IMPORTANT for GA revenue)

        items: [
          {
            item_id: targetCard.giftCard?.id || '',
            item_name: targetCard.giftCard?.name || '',
            item_category:
              targetCard.giftCard?.category?.name ||
              targetCard.giftCard?.category ||
              undefined,
            item_brand: targetCard.giftCard?.name || undefined,

            price: denomination,      // per unit price
            quantity: quantity,       // correct quantity
            discount: discountAmount || undefined, // ✅ total discount applied
          },
        ],
      };
      console.log("Remove from cart event payload:", eventPayload);
      GoogleAnalytics.removeFromCart(eventPayload);
    },

    clear() {
      this.items = [],
        this.cartId = null,
        this.transaction_id = null
    },

    async requestQuote() {
      this.loader = true;
      this.loadingMessage = "Updating cart details...";
      this.response.error = null;

      if (this.items.length === 0) {
        this.response.error = "No items in the cart.";
        this.loader = false;
        return;
      }

      try {
        const auth = useAuth();
        const isAuthenticated = auth && auth.access_token;
        const userData = isAuthenticated ? {
          user_name: auth.user.first_name,
          user_phone: auth.user.phone,
          user_email: auth.user.email,
          user_id: auth.access_token,
          partner: auth.user.partner
        } : {
          user_name: 'Guest User',
          user_phone: null,
          user_email: null
        };
        const location = useLocation();

        const secretData = await useSecret(['earnest_company_id']);
        const encryptedCompanyId = secretData.earnest_company_id;
        if (!encryptedCompanyId) {
          throw new Error("earnest_company_id not found in secretData");
        }
        const decryptedCompanyId = decrypt(encryptedCompanyId);
        const updatedItems = this.items?.[0]?.selectedDenominations?.map((item: selectedDenomination) => {
          const { denominationId, ...rest } = item;
          return rest;
        }) || [];
        const giftDetails = this.items.map(item => ({
          giftCardId: item.giftCard?.id, 
          selectedDenominations: item.selectedDenominations,
          receiverName: item.receiverName,
          recipientEmail: item.recipientEmail,
          recipientPhone: item.recipientPhone,
          senderEmail: item.senderEmail,
          senderPhone: item.senderPhone,
          message: item.message || ""
        }));
        const payload = {
          cartId: this.cartId,
          giftCardData: {
            companyId: decryptedCompanyId,
            buyerPhNumber: this.items?.[0]?.recipientPhone || this.items?.[0]?.senderPhone,
            buyerEmail: this.items?.[0]?.recipientEmail || this.items?.[0]?.senderEmail,
            address: "xyz",
            city: "xyz",
            state: "xyz",
            items: updatedItems,
          },
          user: userData,
          giftDetails
        };

        const { data: quoteData, error: quoteError } = await useGCAPIHandler(
          "post",
          "/earnest/company_order/getQuote",
          { body: payload }
        );

        const responseStatus = quoteData?.status || "error";
        const message =
          quoteData?.message || quoteData?.data?.message || "";

         // Handle backend validation errors (VALIDATION_ERROR)
        if (quoteData?.success === false && quoteData?.code === "VALIDATION_ERROR") {
          this.response.error = quoteData.message;
          return { error: quoteData.message };
        }

        // Check for specific wallet error
        if (message.toLowerCase().includes("insufficient wallet balance")) {
          this.response.error =
            "There’s an issue on our end. We’re working to resolve it as quickly as possible. Please try again shortly. Thank you for your understanding!";
          return { error: this.response.error };
        }

        // Handle Axios/network error
        if (quoteError) {
          throw new Error(`Quote API Error: ${quoteError.message}`);
        }

        // Handle server-declared error response
        if (responseStatus === "error") {
          throw new Error(message || "Quote API returned an error.");
        }

        // Validate presence of quoteResponse and quote data
        const quoteResponse = quoteData?.quoteResponse;
        const breakupItems = quoteResponse?.data?.quote?.breakup ?? [];

        if (breakupItems.length === 0) {
          throw new Error("Quote response missing item details.");
        }

        // Save required info
        this.transaction_id = quoteResponse.data.transaction_id || null;
        this.response.data = quoteResponse;

        return quoteResponse;

      } catch (error: any) {
        console.error("Error in requestQuote:", error.message);
        if (!this.response.error) {
          this.response.error =
            "The selected gift card is currently unavailable. Please choose a different one.";
        }
        return { error: error.message };
      } finally {
        this.loader = false;
        this.loadingMessage = "";
      }
    },

    async submitOrder(orderDetails: { data: any; user_id: string }) {
      this.loader = true;
      this.loadingMessage = "Payment successful! Processing your order...";
      this.response.error = null;

      try {
        // Step 1: Prepare status payload
        const statusPayload = {
          data: {
            transactionId: orderDetails.data.transactionId,
          },
          user_id: orderDetails.user_id,
          cartId: this.cartId,
        };

        this.loadingMessage = "Verifying your order status...";

        // Step 2: Check order status
        const statusResponse = await this.checkOrderStatus(statusPayload);

        const vouchers = statusResponse?.result?.vouchers || [];
        const hasValidVoucher = vouchers.some(
          (voucher: any) =>
            typeof voucher.voucherCode === "string" && voucher.voucherCode.trim() !== ""
        );

        const transactionId = orderDetails.data.transactionId;

        // Step 3: Store gift card details
        const { data: response, error } = await useGCAPIHandler("post", "/earnest/giftCardDetails", {
          body: statusPayload,
        });

        if (error) {
          throw new Error(`Failed to insert gift card details: ${error.message}`);
        }

        // Step 4: Handle email status or fallback
        if (response?.success === true) {
          console.log("✅ Gift card details saved successfully.");
        } else {
          console.warn("⚠️ Gift card email send failed or was skipped.");
        }

        // Step 5: Navigate to summary regardless
        // navigateTo(`/collections/gift-cards/order-summary/${transactionId}`);
        this.clear();
        this.loadingMessage = "";
        this.loader = false;

        return response;

      } catch (error: any) {
        console.error("❌ Order Submission Failed:", error.message);

        this.response.error =
          "Payment was successful, but the order failed due to a processing error. A refund will reflect in 3–5 business days. Please contact support if needed.";

        this.loader = false;
        this.loadingMessage = "";
        this.clear();

        return null;
      }
    },

    async checkOrderStatus(statusPayload: { data: any; user_id: string }) {
      this.loader = true;
      this.response.error = null;

      let attempts = 0;
      let lastStatusData: any = null;
      const maxRetries = 6;
      const retryDelay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

      const messages = [
        "Verifying your order status... Please be patient.",
        "Still checking with the brand. Hang tight...",
        "Processing your order. Almost there...",
        "Confirming voucher with provider...",
        "Double-checking everything. One moment...",
        "Final check in progress. Thank you for waiting.",
      ];

      while (attempts < maxRetries) {
        this.loadingMessage = messages[attempts] || messages[messages.length - 1];

        try {
          const { data: statusData, error: statusError } = await useGCAPIHandler(
            "post",
            "/earnest/company_order/get-voucher-status",
            { body: statusPayload }
          );

          if (statusError || !statusData || statusData.message?.toLowerCase() !== "success") {
            throw new Error("Invalid or failed response");
          }

          lastStatusData = statusData;

          const resultStatus = statusData?.result?.status?.toLowerCase();
          const lineStatuses: string[] = (statusData?.result?.lineStatus ?? []).map(
            (line: { status: string }) => line.status.toLowerCase()
          );
          const vouchers = statusData?.result?.vouchers ?? [];

          const firstValidVoucher = vouchers.find(
            (voucher: { voucherCode?: string }) =>
              typeof voucher.voucherCode === "string" && voucher.voucherCode.trim() !== ""
          );

          // ✅ Cancelled — fail early, do not retry
          if (lineStatuses.length > 0 && lineStatuses.every((s) => s === "cancelled")) {
            this.loadingMessage = "Looks like your order was cancelled! Let’s take you to your order.";
            this.loader = false;
            return statusData;
          }

          // ✅ Fully successful — exit early
          if (
            resultStatus === "success" &&
            lineStatuses.every((s) => s === "success") &&
            firstValidVoucher
          ) {
            this.loadingMessage = "✅ Voucher confirmed...";
            this.loader = false;
            return statusData;
          }

          // ⏳ Still processing — continue retry
          if (
            resultStatus === "processing" ||
            lineStatuses.every((s) => s === "pending" || s === "processing") ||
            !firstValidVoucher
          ) {
            attempts++;
            await retryDelay(3000);
            continue;
          }

          // 🤫 Unknown state — retry cautiously
          attempts++;
          await retryDelay(3000);
        } catch (error) {
          // Silent fail, keep user calm
          attempts++;
          await retryDelay(3000);
        }
      }

      // ✅ After max retries — fallback gracefully
      this.loadingMessage = "Thanks for waiting! Let’s take you to your order.";
      this.loader = false;
      return lastStatusData;
    }
  },
  persist: {
    storage: piniaPluginPersistedstate.localStorage(),
  },
});
